import { zip } from "fflate";

export const downloadJSON = (filename, data) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// DownloadSurveyJson (and similar APIs) return the file body as a base64
// string alongside its contentType/fileName rather than a binary response,
// so the browser download has to be built from the decoded bytes locally.
export const downloadBase64File = (base64Content, filename, contentType) => {
  const bytes = Uint8Array.from(atob(base64Content), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: contentType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Fetches a remote file and triggers a browser download for it (as opposed
// to navigating to the URL directly, which would open it in a new tab).
export const downloadBlobUrl = async (url, filename) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

// Fetches multiple remote files, zips them together in the browser, and
// downloads the archive as one file — used so the backend never has to
// persist a zip in S3 just to bundle a CSV with its log file.
// onProgress(percent) is called as bytes stream in across all files (0-99;
// reserves 100 for once the zip archive itself has been built).
export const downloadFilesAsZip = async (files, zipFilename, onProgress) => {
  const totals = new Array(files.length).fill(0);
  const loaded = new Array(files.length).fill(0);

  const reportProgress = () => {
    if (!onProgress) return;
    const totalBytes = totals.reduce((a, b) => a + b, 0);
    if (!totalBytes) return; // no Content-Length to measure against yet
    const loadedBytes = loaded.reduce((a, b) => a + b, 0);
    onProgress(Math.min(99, Math.round((loadedBytes / totalBytes) * 100)));
  };

  const entries = await Promise.all(
    files.map(async ({ url, filename }, i) => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      totals[i] = Number(response.headers.get("content-length")) || 0;

      const reader = response.body.getReader();
      const chunks = [];
      let received = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        loaded[i] = received;
        reportProgress();
      }

      const buffer = new Uint8Array(received);
      let offset = 0;
      for (const chunk of chunks) {
        buffer.set(chunk, offset);
        offset += chunk.length;
      }
      return [filename, buffer];
    }),
  );

  const zipped = await new Promise((resolve, reject) => {
    zip(Object.fromEntries(entries), (err, result) => (err ? reject(err) : resolve(result)));
  });
  onProgress?.(100);

  const blob = new Blob([zipped], { type: "application/zip" });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = zipFilename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};
